import React, { useState, useEffect } from "react";
import "../WithdrawalStyle.css";
import config from '../config';
import { useTheme } from '../contexts/ThemeContext';
import { Visibility, VisibilityOff, Edit, Save, Cancel, AccountBalance, Person, Phone, AttachMoney } from '@mui/icons-material';

const Withdrawal = () => {    
  const user_id = Number(localStorage.getItem('userId'));
  const { isDarkMode } = useTheme();

  const [userData, setUserData] = useState({
    clientId: "",
    parentId: "",
    clientName: "",
    phoneNumber: "",
    supportContact: "",
    withdrawContact: "",
    address: "",
    mainAmount: "0",
    marginUsed: "0",
    marginAvailable: "0",
    availableMargin: "0",
    bank: {
      accountHolderName: "",
      accountNumber: "",
      aacountType: "SAVING",
      IFSC: "",
      bank: "",
      branch: "",
      upiHandle: ""
    }
  });

  const [formData, setFormData] = useState({
    name: "",
    userId: "",
    phone: "",
    amount: "",
    remark: "",
  });

  const [bankDetails, setBankDetails] = useState({
    bank: "",
    branch: "",
    accountHolderName: "",
    accountNumber: "",
    reAccountNumber: "",
    ifsc: "",
    accountType: "SAVING",
    upiHandle: ""
  });

  const [amountInWords, setAmountInWords] = useState("");
  const [hasPositions, setHasPositions] = useState(false);
  const [numberOfPositions, setNumberOfPositions] = useState(0);
  const [isEditingBankDetails, setIsEditingBankDetails] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('withdrawal'); // 'withdrawal' or 'bank'

  const getUserDetails = async () => {
    try {  
      const response = await fetch(`${config.API_BASE_URL}getUserDetails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ user_id }),
      });
      
      const data = await response.json();
      
      // Store complete user data
      setUserData({
        clientId: data.clientId,
        parentId: data.parentId,
        clientName: data.clientName,
        phoneNumber: data.phoneNumber,
        supportContact: data.supportContact,
        withdrawContact: data.withdrawContact,
        address: data.address,
        mainAmount: data.mainAmount,
        marginUsed: data.marginUsed,
        marginAvailable: data.marginAvailable,
        availableMargin: data.availableMargin,
        bank: {
          accountHolderName: data.accountHolderName,
          accountNumber: data.accountNumber,
          aacountType: data.aacountType,
          IFSC: data.IFSC,
          bank: data.bank,
          branch: data.branch,
          upiHandle: data.upiHandle
        }
      });

      // Update form data
      setFormData(prevFormData => ({
        ...prevFormData,
        name: data.clientName,
        userId: data.clientId,
        phone: data.phoneNumber
      }));

      // Update bank details
      setBankDetails({
        bank: data.bank || "",
        branch: data.branch || "",
        accountHolderName: data.accountHolderName || "",
        accountNumber: data.accountNumber || "",
        reAccountNumber: "",
        ifsc: data.IFSC || "",
        accountType: data.aacountType || "SAVING",
        upiHandle: data.upiHandle || ""
      });

      // Check for active positions
      setHasPositions(data.hasPositions || false);
      setNumberOfPositions(data.numberOfPositions || 0);
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  useEffect(() => {
    getUserDetails();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Convert amount to words
    if (name === 'amount' && value) {
      const amount = parseFloat(value);
      if (!isNaN(amount)) {
        setAmountInWords(convertToWords(amount));
      } else {
        setAmountInWords("");
      }
    }
  };

  const handleBankDetailsChange = (e) => {
    const { name, value } = e.target;
    setBankDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const convertToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

    if (num === 0) return 'Zero';
    if (num < 0) return 'Negative ' + convertToWords(-num);

    let result = '';

    // Handle Crores
    if (num >= 10000000) {
      const crores = Math.floor(num / 10000000);
      result += convertToWords(crores) + ' Crore ';
      num %= 10000000;
    }

    // Handle Lakhs
    if (num >= 100000) {
      const lakhs = Math.floor(num / 100000);
      result += convertToWords(lakhs) + ' Lakh ';
      num %= 100000;
    }

    // Handle Thousands
    if (num >= 1000) {
      const thousands = Math.floor(num / 1000);
      result += convertToWords(thousands) + ' Thousand ';
      num %= 1000;
    }

    // Handle Hundreds
    if (num >= 100) {
      result += ones[Math.floor(num / 100)] + ' Hundred ';
      num %= 100;
    }

    // Handle Tens and Ones
    if (num >= 20) {
      result += tens[Math.floor(num / 10)] + ' ';
      num %= 10;
    } else if (num >= 10) {
      result += teens[num - 10] + ' ';
      num = 0;
    }

    if (num > 0) {
      result += ones[num] + ' ';
    }

    return result.trim() + ' Rupees Only';
  };

  const handleWithdrawalRequest = async () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    const message = `I want to withdraw ₹${formData.amount}\nMy Client ID: ${formData.userId}\nClient Name: ${formData.name}\nBank: ${bankDetails.bank}\nBank account: ${bankDetails.accountNumber}\nIFSC: ${bankDetails.ifsc}\nRemark: ${formData.remark}`;
    const phoneNumber = userData.withdrawContact;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleBankDetailsSubmit = async (e) => {
    e.preventDefault();
    
    if (bankDetails.accountNumber !== bankDetails.reAccountNumber) {
      alert('Account numbers do not match');
      return;
    }

    try {
      const response = await fetch(`${config.API_BASE_URL}updateBankDetails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          accountHolderName: bankDetails.accountHolderName,
          accountNumber: bankDetails.accountNumber,
          aacountType: bankDetails.accountType,
          IFSC: bankDetails.ifsc,
          bank: bankDetails.bank,
          branch: bankDetails.branch,
          upiHandle: bankDetails.upiHandle,
          clientId: user_id
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        alert('Bank details updated successfully');
        setIsEditingBankDetails(false);
        getUserDetails(); // Refresh user data
      } else {
        alert('Error: ' + (data.message || 'Failed to update bank details'));
      }
    } catch (error) {
      console.error('Error updating bank details:', error);
      alert('Error updating bank details');
    }
  };

  return (
    <div className={`withdrawal-page ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* Header Section */}
      <div className="withdrawal-header">
        <h1 className="page-title">Withdrawal Request</h1>
        <p className="page-subtitle">Manage your withdrawal requests and bank details</p>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'withdrawal' ? 'active' : ''}`}
          onClick={() => setActiveTab('withdrawal')}
        >
          <AttachMoney className="tab-icon" />
          Withdrawal Request
        </button>
        <button 
          className={`tab-button ${activeTab === 'bank' ? 'active' : ''}`}
          onClick={() => setActiveTab('bank')}
        >
          <AccountBalance className="tab-icon" />
          Bank Details
        </button>
      </div>

      {/* Withdrawal Request Tab */}
      {activeTab === 'withdrawal' && (
        <div className="withdrawal-section">
          <div className="form-card">
            <div className="card-header">
              <h2 className="card-title">Withdrawal Request Form</h2>
            </div>

            {hasPositions && (
              <div className="warning-banner">
                <div className="warning-icon">⚠️</div>
                <div className="warning-content">
                  <strong>Active Positions Detected</strong>
                  <p>You have {numberOfPositions} active position{numberOfPositions > 1 ? "s" : ""} in your portfolio. Please close all positions before requesting withdrawal.</p>
                  <a href="/portfolio" className="warning-link">Go to Portfolio</a>
                </div>
              </div>
            )}

            <form className="withdrawal-form" onSubmit={(e) => e.preventDefault()}>
              {/* User Info Section */}
              <div className="form-section">
                <h3 className="section-title">Account Information</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">
                      <Person className="label-icon" />
                      Full Name
                    </label>
                    <input 
                      type="text" 
                      name="name" 
                      value={formData.name} 
                      readOnly 
                      className="form-input disabled"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      <AccountBalance className="label-icon" />
                      User ID
                    </label>
                    <input 
                      type="text" 
                      name="userId" 
                      value={formData.userId} 
                      readOnly 
                      className="form-input disabled"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      <Phone className="label-icon" />
                      Phone Number
                    </label>
                    <input 
                      type="text" 
                      name="phone" 
                      value={formData.phone} 
                      readOnly 
                      className="form-input disabled"
                    />
                  </div>
                </div>
              </div>

              {/* Withdrawal Details Section */}
              <div className="form-section">
                <h3 className="section-title">Withdrawal Details</h3>
                <div className="form-group">
                  <label className="form-label">
                    <AttachMoney className="label-icon" />
                    Withdrawal Amount
                  </label>
                  <input 
                    type="number" 
                    name="amount" 
                    value={formData.amount} 
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter withdrawal amount"
                    min="1"
                    disabled={hasPositions}
                    required
                  />
                  {amountInWords && (
                    <div className="amount-in-words">
                      <strong>Amount in words:</strong> {amountInWords}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Additional Remarks</label>
                  <textarea 
                    name="remark" 
                    value={formData.remark} 
                    onChange={handleInputChange}
                    className="form-textarea"
                    placeholder="Enter any additional remarks (optional)"
                    rows="3"
                    disabled={hasPositions}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={handleWithdrawalRequest}
                  disabled={hasPositions}
                  className={`submit-button ${hasPositions ? 'disabled' : ''}`}
                >
                  Submit Withdrawal Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bank Details Tab */}
      {activeTab === 'bank' && (
        <div className="bank-section">
          <div className="form-card">
            <div className="card-header">
              <h2 className="card-title">Bank Account Details</h2>
              <button 
                onClick={() => setIsEditingBankDetails(!isEditingBankDetails)}
                className={`edit-button ${isEditingBankDetails ? 'cancel' : 'edit'}`}
              >
                {isEditingBankDetails ? (
                  <>
                    <Cancel className="button-icon" />
                    Cancel
                  </>
                ) : (
                  <>
                    <Edit className="button-icon" />
                    Edit Details
                  </>
                )}
              </button>
            </div>

            {isEditingBankDetails ? (
              <form className="bank-form" onSubmit={handleBankDetailsSubmit}>
                <div className="form-section">
                  <h3 className="section-title">Bank Information</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Bank Name</label>
                      <input 
                        type="text" 
                        name="bank" 
                        value={bankDetails.bank} 
                        onChange={handleBankDetailsChange}
                        className="form-input"
                        placeholder="Enter bank name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Branch</label>
                      <input 
                        type="text" 
                        name="branch" 
                        value={bankDetails.branch} 
                        onChange={handleBankDetailsChange}
                        className="form-input"
                        placeholder="Enter branch name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">IFSC Code</label>
                      <input 
                        type="text" 
                        name="ifsc" 
                        value={bankDetails.ifsc} 
                        onChange={handleBankDetailsChange}
                        className="form-input"
                        placeholder="Enter IFSC code"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Account Type</label>
                      <select 
                        name="accountType" 
                        value={bankDetails.accountType} 
                        onChange={handleBankDetailsChange}
                        className="form-input"
                        required
                      >
                        <option value="SAVING">Savings Account</option>
                        <option value="CURRENT">Current Account</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3 className="section-title">Account Holder Information</h3>
                  <div className="form-group">
                    <label className="form-label">Account Holder Name</label>
                    <input 
                      type="text" 
                      name="accountHolderName" 
                      value={bankDetails.accountHolderName} 
                      onChange={handleBankDetailsChange}
                      className="form-input"
                      placeholder="Enter account holder name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Account Number</label>
                    <input 
                      type="text" 
                      name="accountNumber" 
                      value={bankDetails.accountNumber} 
                      onChange={handleBankDetailsChange}
                      className="form-input"
                      placeholder="Enter account number"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Re-enter Account Number</label>
                    <input 
                      type="text" 
                      name="reAccountNumber" 
                      value={bankDetails.reAccountNumber} 
                      onChange={handleBankDetailsChange}
                      className="form-input"
                      placeholder="Re-enter account number"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">UPI Handle (Optional)</label>
                    <input 
                      type="text" 
                      name="upiHandle" 
                      value={bankDetails.upiHandle} 
                      onChange={handleBankDetailsChange}
                      className="form-input"
                      placeholder="Enter UPI handle"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button 
                    type="submit"
                    className="submit-button"
                  >
                    <Save className="button-icon" />
                    Save Bank Details
                  </button>
                </div>
              </form>
            ) : (
              <div className="bank-details-display">
                <div className="bank-details-grid">
                  <div className="bank-detail-item">
                    <div className="detail-label">Bank Name</div>
                    <div className="detail-value">{userData.bank.bank || 'Not provided'}</div>
                  </div>
                  <div className="bank-detail-item">
                    <div className="detail-label">Branch</div>
                    <div className="detail-value">{userData.bank.branch || 'Not provided'}</div>
                  </div>
                  <div className="bank-detail-item">
                    <div className="detail-label">Account Holder</div>
                    <div className="detail-value">{userData.bank.accountHolderName || 'Not provided'}</div>
                  </div>
                  <div className="bank-detail-item">
                    <div className="detail-label">Account Number</div>
                    <div className="detail-value">
                      {userData.bank.accountNumber ? '****' + userData.bank.accountNumber.slice(-4) : 'Not provided'}
                    </div>
                  </div>
                  <div className="bank-detail-item">
                    <div className="detail-label">IFSC Code</div>
                    <div className="detail-value">{userData.bank.IFSC || 'Not provided'}</div>
                  </div>
                  <div className="bank-detail-item">
                    <div className="detail-label">Account Type</div>
                    <div className="detail-value">{userData.bank.aacountType || 'Not provided'}</div>
                  </div>
                  <div className="bank-detail-item">
                    <div className="detail-label">UPI Handle</div>
                    <div className="detail-value">{userData.bank.upiHandle || 'Not provided'}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Withdrawal;